import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabaseService } from '../../utils/supabaseService';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const PublicArtistDetail = () => {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const [contactStatus, setContactStatus] = useState(null); // statut affiché sous le bloc bio

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await supabaseService.getPublicArtist(id);
        setArtist(data);
        // Charger disponibilité publique (prochaines 30j)
        const today = new Date();
        const in30 = new Date(Date.now() + 30*24*60*60*1000);
        const { data: avail, error: aErr } = await supabase
          .from('artist_availability')
          .select('date,status')
          .eq('artist_id', id)
          .eq('status','available')
          .gte('date', today.toISOString().slice(0,10))
          .lte('date', in30.toISOString().slice(0,10))
          .order('date');
        if (!aErr) setAvailability(avail || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="pt-24 p-6 text-sm">Chargement...</div>;
  if (error) return <div className="pt-24 p-6 text-sm text-red-500">{error}</div>;
  if (!artist) return <div className="pt-24 p-6 text-sm text-muted-foreground">Artiste introuvable ou privé.</div>;

  const links = artist.social_links || {};

  return (
    <div className="pt-24 pb-16 max-w-4xl mx-auto px-4">
      <div className="flex items-center mb-6">
        <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mr-4">
          <Icon name="Music" size={32} color="white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-1">{artist.display_name || 'Sans nom'}</h1>
          <div className="text-sm text-muted-foreground">{artist.city || 'Ville'}{artist.country ? ', ' + artist.country : ''}</div>
        </div>
        {user && user.id !== artist.user_id && (
          <div className="ml-auto">
            <Button size="sm" variant="outline" onClick={async ()=>{
              try {
                setContactStatus('Création de la conversation...');
                await supabaseService.getOrCreateDirectConversation({ userId: user.id, otherUserId: artist.user_id });
                setContactStatus('Conversation créée. Rendez-vous dans la messagerie.');
              } catch(e){ setContactStatus(e.message); }
            }}>Contacter</Button>
          </div>
        )}
      </div>
      {contactStatus && <div className="text-xs text-muted-foreground mb-4">{contactStatus}</div>}
      <div className="mb-8 grid md:grid-cols-3 gap-4">
        <div className="border border-border rounded-lg p-4">
          <div className="font-semibold mb-1">Genres</div>
          <div className="text-xs text-muted-foreground">{artist.genres?.length ? artist.genres.join(', ') : '—'}</div>
        </div>
        <div className="border border-border rounded-lg p-4">
          <div className="font-semibold mb-1">Cachet</div>
          <div className="text-xs text-muted-foreground">{artist.cachet_min ? artist.cachet_min + (artist.cachet_max ? ' - ' + artist.cachet_max : '') + ' €' : '—'}</div>
        </div>
        <div className="border border-border rounded-lg p-4">
          <div className="font-semibold mb-1">Mise à jour</div>
          <div className="text-xs text-muted-foreground">{new Date(artist.created_at).toLocaleDateString()}</div>
        </div>
      </div>
      {artist.bio && (
        <div className="mb-10">
          <h2 className="text-xl font-semibold mb-2">Bio</h2>
          <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{artist.bio}</p>
        </div>
      )}
      <h2 className="text-xl font-semibold mb-3">Liens</h2>
      <div className="flex flex-wrap gap-3 mb-10">
        {Object.keys(links).length === 0 && <div className="text-xs text-muted-foreground">Aucun lien renseigné.</div>}
        {Object.entries(links).map(([k,v]) => v && (
          <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs rounded-md border border-border hover:bg-muted transition-colors">
            {k}
          </a>
        ))}
      </div>
      <Button variant="accent" size="sm">Proposer une date</Button>
      <div className="mt-12">
        <h2 className="text-xl font-semibold mb-3">Disponibilités (30j)</h2>
        {availability.length === 0 && <div className="text-xs text-muted-foreground">Aucune disponibilité publique enregistrée.</div>}
        {availability.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availability.map(a => (
              <span key={a.date} className="px-2 py-1 text-xs border border-border rounded-md bg-card">{new Date(a.date).toLocaleDateString()}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicArtistDetail;